/**
 * FairScoreComparison Component
 *
 * Displays a side-by-side comparison of FMCSA SMS scores vs Bayesian Fair Scores.
 * Shows how bias correction affects the carrier's safety assessment.
 */

import React, { useEffect, useState } from 'react';
import { Scale, TrendingUp, TrendingDown, Minus, AlertCircle, CheckCircle, Info, Shield } from 'lucide-react';
import { carrierApi } from '../../services/api';
import type { FairScoreResponse, FairScore, BASICScore } from '../../types/api';

interface FairScoreComparisonProps {
  dotNumber: number;
  basics: BASICScore[];
  onViewDetails?: () => void;
}

const BASIC_NAMES: Record<string, string> = {
  'BASIC-11': 'Unsafe Driving',
  'BASIC-12': 'HOS Compliance',
  'BASIC-13': 'Driver Fitness',
  'BASIC-14': 'Drugs/Alcohol',
  'BASIC-15': 'Vehicle Maint.',
  'BASIC-16': 'Hazmat',
  'BASIC-17': 'Crash Indicator',
};

const getScoreColor = (score: number | null): string => {
  if (score === null) return 'var(--color-gray-400)';
  if (score >= 80) return 'var(--color-danger)';
  if (score >= 65) return 'var(--color-warning)';
  return 'var(--color-success)';
};

const getScoreLabel = (score: number | null): string => {
  if (score === null) return 'N/A';
  if (score >= 80) return 'Poor';
  if (score >= 65) return 'Marginal';
  if (score >= 50) return 'Average';
  return 'Good';
};

const getConfidenceBadge = (level: string) => {
  const colors: Record<string, { bg: string; text: string }> = {
    HIGH: { bg: 'rgba(16, 185, 129, 0.1)', text: 'var(--color-success)' },
    MEDIUM: { bg: 'rgba(245, 158, 11, 0.1)', text: 'var(--color-warning)' },
    LOW: { bg: 'rgba(239, 68, 68, 0.1)', text: 'var(--color-danger)' },
  };
  return colors[level] || colors.MEDIUM;
};

export const FairScoreComparison: React.FC<FairScoreComparisonProps> = ({
  dotNumber,
  basics,
  onViewDetails,
}) => {
  const [fairScores, setFairScores] = useState<FairScoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFairScores = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await carrierApi.getFairScores(dotNumber);
        setFairScores(response);
      } catch (err) {
        // Not an error if no scores available - just means carrier hasn't been processed
        setFairScores(null);
      } finally {
        setLoading(false);
      }
    };

    if (dotNumber) {
      fetchFairScores();
    }
  }, [dotNumber]);

  // Create a map of SMS scores by BASIC ID for lookup.
  // Backend returns items shaped as { basicsType: { basicsCode, basicsId }, basicsPercentile }.
  // basicsCode is the human name (e.g. "Unsafe Driving"), basicsId is the numeric id (11-17).
  // basicsPercentile may be the literal "Not Public" for FMCSA-suppressed BASICs (e.g. Crash Indicator).
  const smsScoreMap = new Map<string, number | null | 'Not Public'>();
  (basics || []).forEach((basic: any) => {
    const code: string | undefined =
      basic?.basicsType?.basicsCode && /^BASIC-\d+$/.test(basic.basicsType.basicsCode)
        ? basic.basicsType.basicsCode
        : basic?.basicsType?.basicsId != null
          ? `BASIC-${basic.basicsType.basicsId}`
          : basic?.basic_id
            ? (typeof basic.basic_id === 'string' && basic.basic_id.startsWith('BASIC-')
                ? basic.basic_id
                : `BASIC-${basic.basic_id}`)
            : undefined;
    if (!code) return;
    const raw = basic?.basicsPercentile ?? basic?.percentile;
    if (raw === 'Not Public') {
      smsScoreMap.set(code, 'Not Public');
    } else if (raw === null || raw === undefined || raw === '') {
      smsScoreMap.set(code, null);
    } else {
      const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
      smsScoreMap.set(code, isNaN(n) ? null : n);
    }
  });

  // Use fair scores as the primary source - show all calculated fair scores
  // and join with SMS scores where available
  const comparisons = (fairScores?.scores || []).map(score => {
    const rawSms = smsScoreMap.get(score.basicId);
    const smsPercentile: number | null = typeof rawSms === 'number' ? rawSms : null;
    const smsNotPublic = rawSms === 'Not Public';
    return {
      basicId: score.basicId,
      name: BASIC_NAMES[score.basicId] || score.basicId,
      smsPercentile,
      smsNotPublic,
      fairScore: score.fairScore,
      fairScoreLower: score.fairScoreLower,
      fairScoreUpper: score.fairScoreUpper,
      confidence: score.confidenceLevel,
      difference: smsPercentile !== null ? smsPercentile - (100 - score.fairScore) : null,
    };
  });

  // Calculate overall comparison
  const hasComparisons = comparisons.some(c => c.fairScore !== null);
  const avgSmsDiff = hasComparisons
    ? comparisons
        .filter(c => c.difference !== null)
        .reduce((sum, c) => sum + Math.abs(c.difference!), 0) /
      comparisons.filter(c => c.difference !== null).length
    : 0;

  if (loading) {
    return (
      <div
        className="card-modern"
        style={{
          padding: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
        }}
      >
        <div style={{ textAlign: 'center', color: 'var(--color-gray-500)' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              border: '3px solid var(--color-gray-200)',
              borderTopColor: 'var(--color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto var(--space-sm)',
            }}
          />
          Loading fair scores...
        </div>
      </div>
    );
  }

  if (!fairScores || fairScores.scores.length === 0) {
    return (
      <div
        className="card-modern"
        style={{
          padding: 'var(--space-lg)',
          background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%)',
          border: '1px solid rgba(37, 99, 235, 0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: 'var(--radius-lg)',
              background: 'linear-gradient(135deg, var(--color-primary) 0%, #9333ea 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Scale size={24} color="white" />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
              Fair Assessment Coming Soon
            </h3>
            <p style={{ margin: 'var(--space-xs) 0 0', fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
              Bayesian fair scores for this carrier are not yet available. Our system processes carriers periodically
              to generate bias-corrected safety assessments.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="card-modern"
      style={{
        padding: 'var(--space-lg)',
        background: 'white',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-lg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: 'var(--radius-md)',
              background: 'linear-gradient(135deg, var(--color-primary) 0%, #9333ea 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Scale size={20} color="white" />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>FMCSA vs Fair Score</h3>
            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
              Bias-corrected safety assessment
            </p>
          </div>
        </div>
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            style={{
              padding: 'var(--space-xs) var(--space-sm)',
              background: 'var(--color-gray-100)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.75rem',
              color: 'var(--color-primary)',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            View Details
          </button>
        )}
      </div>

      {/* Summary Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'var(--space-sm)',
          marginBottom: 'var(--space-lg)',
          padding: 'var(--space-md)',
          background: 'var(--color-gray-50)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)' }}>
            {fairScores.summary.overallFairScore ?? 'N/A'}
            <span style={{ fontSize: '0.9rem', fontWeight: 400, color: 'var(--color-gray-500)' }}>/100</span>
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--color-gray-500)', textTransform: 'uppercase' }}>
            Fair Score
          </div>
          <div style={{ fontSize: '0.65rem', color: 'var(--color-gray-400)', marginTop: '2px' }}>
            Higher = Safer
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-success)' }}>
            {fairScores.summary.highConfidenceCount}/{fairScores.scores.length}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--color-gray-500)', textTransform: 'uppercase' }}>
            High Confidence
          </div>
          <div style={{ fontSize: '0.65rem', color: 'var(--color-gray-400)', marginTop: '2px' }}>
            BASICs Scored
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-700)' }}>
            ±{Math.round(fairScores.summary.averageCredibleIntervalWidth / 2)}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--color-gray-500)', textTransform: 'uppercase' }}>
            Uncertainty
          </div>
          <div style={{ fontSize: '0.65rem', color: 'var(--color-gray-400)', marginTop: '2px' }}>
            95% CI Range
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
              <th style={{ textAlign: 'left', padding: 'var(--space-sm)', fontWeight: 600, color: 'var(--color-gray-600)' }}>
                BASIC
              </th>
              <th style={{ textAlign: 'center', padding: 'var(--space-sm)', fontWeight: 600, color: 'var(--color-gray-600)' }}>
                FMCSA %ile
              </th>
              <th style={{ textAlign: 'center', padding: 'var(--space-sm)', fontWeight: 600, color: 'var(--color-gray-600)' }}>
                Fair Score
              </th>
              <th style={{ textAlign: 'center', padding: 'var(--space-sm)', fontWeight: 600, color: 'var(--color-gray-600)' }}>
                Confidence
              </th>
              <th style={{ textAlign: 'center', padding: 'var(--space-sm)', fontWeight: 600, color: 'var(--color-gray-600)' }}>
                Impact
              </th>
            </tr>
          </thead>
          <tbody>
            {comparisons.map(comp => {
              const hasData = comp.fairScore !== null;
              // Fair score is inverted (higher = safer), SMS percentile is higher = worse
              // To compare, we invert the fair score: 100 - fairScore gives "risk percentile"
              const fairRiskPercentile = hasData ? 100 - comp.fairScore! : null;
              const diff = hasData && comp.smsPercentile !== null
                ? comp.smsPercentile - fairRiskPercentile!
                : null;

              return (
                <tr
                  key={comp.basicId}
                  style={{
                    borderBottom: '1px solid var(--color-gray-100)',
                    background: hasData ? 'white' : 'var(--color-gray-50)',
                  }}
                >
                  <td style={{ padding: 'var(--space-sm)', fontWeight: 500 }}>
                    {comp.name}
                  </td>
                  <td style={{ textAlign: 'center', padding: 'var(--space-sm)' }}>
                    {comp.smsPercentile !== null ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-sm)',
                          background: getScoreColor(comp.smsPercentile) + '20',
                          color: getScoreColor(comp.smsPercentile),
                          fontWeight: 600,
                          fontSize: '0.8rem',
                        }}
                      >
                        {comp.smsPercentile}%
                      </span>
                    ) : comp.smsNotPublic ? (
                      <span style={{ color: 'var(--color-gray-500)', fontSize: '0.75rem', fontStyle: 'italic' }}>Not Public</span>
                    ) : (
                      <span style={{ color: 'var(--color-gray-400)' }}>N/A</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', padding: 'var(--space-sm)' }}>
                    {hasData ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--color-primary)' + '20',
                            color: 'var(--color-primary)',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                          }}
                        >
                          {typeof comp.fairScore === 'number' ? comp.fairScore.toFixed(1) : comp.fairScore}
                        </span>
                        <span style={{ fontSize: '0.65rem', color: 'var(--color-gray-400)', marginTop: '2px' }}>
                          ({typeof comp.fairScoreLower === 'number' ? comp.fairScoreLower.toFixed(1) : comp.fairScoreLower}-{typeof comp.fairScoreUpper === 'number' ? comp.fairScoreUpper.toFixed(1) : comp.fairScoreUpper})
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--color-gray-400)' }}>-</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', padding: 'var(--space-sm)' }}>
                    {comp.confidence ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: 'var(--radius-sm)',
                          background: getConfidenceBadge(comp.confidence).bg,
                          color: getConfidenceBadge(comp.confidence).text,
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        {comp.confidence}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-gray-400)' }}>-</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', padding: 'var(--space-sm)' }}>
                    {diff !== null ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        {diff > 5 ? (
                          <>
                            <TrendingDown size={14} color="var(--color-success)" />
                            <span style={{ color: 'var(--color-success)', fontWeight: 500, fontSize: '0.8rem' }}>
                              -{Math.abs(Math.round(diff))}
                            </span>
                          </>
                        ) : diff < -5 ? (
                          <>
                            <TrendingUp size={14} color="var(--color-danger)" />
                            <span style={{ color: 'var(--color-danger)', fontWeight: 500, fontSize: '0.8rem' }}>
                              +{Math.abs(Math.round(diff))}
                            </span>
                          </>
                        ) : (
                          <>
                            <Minus size={14} color="var(--color-gray-400)" />
                            <span style={{ color: 'var(--color-gray-500)', fontSize: '0.8rem' }}>~0</span>
                          </>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--color-gray-400)' }}>-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Info Footer */}
      <div
        style={{
          marginTop: 'var(--space-md)',
          padding: 'var(--space-sm)',
          background: 'var(--color-gray-50)',
          borderRadius: 'var(--radius-sm)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)',
          fontSize: '0.75rem',
          color: 'var(--color-gray-600)',
        }}
      >
        <Info size={14} />
        <span>
          Fair Scores use Bayesian IRT to correct for fleet size, region, and operation type biases.
          Higher fair score = safer carrier. Impact shows FMCSA percentile vs equivalent fair risk.
        </span>
      </div>
    </div>
  );
};

export default FairScoreComparison;
